import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { Trophy, Crown, Users, Loader2, Zap } from "lucide-react";
import LevelBadge from "@/components/LevelBadge";

const RANK_ACCENT = {
  1: { bg: "#FFB800", ring: "rgba(255,184,0,0.5)", label: "#FFB800" },
  2: { bg: "#C0C0C0", ring: "rgba(192,192,192,0.5)", label: "#C0C0C0" },
  3: { bg: "#B87333", ring: "rgba(184,115,51,0.5)", label: "#B87333" },
};

const PERIOD_LABELS = {
  week: "this week",
  month: "this month",
  all: "all time",
};

export default function GifterLeaderboard({ initialPeriod = "week", compact = false, className = "" }) {
  const [period, setPeriod] = useState(initialPeriod);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/leaderboards/gifters?period=${period}&limit=${compact ? 5 : 10}`)
      .then((r) => {
        if (cancelled) return;
        setRows(r.data?.gifters || []);
      })
      .catch(() => setRows([]))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period, compact]);

  return (
    <section
      data-testid="gifter-leaderboard"
      className={`border border-white/10 bg-[#0A0A0C] ${className}`}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Crown className="h-4 w-4 text-[#FFB800]" strokeWidth={2} fill="#FFB800" />
          <h3 className="font-display font-black text-sm tracking-tight text-white truncate">
            TOP GIFTERS
          </h3>
          <span className="font-mono text-[9px] uppercase tracking-widest text-white/40 hidden sm:inline">
            // {PERIOD_LABELS[period]}
          </span>
        </div>
        <div className="flex bg-black/50 border border-white/10 text-[10px] uppercase font-mono">
          {["week", "month", "all"].map((p) => (
            <button
              key={p}
              data-testid={`leaderboard-period-${p}`}
              onClick={() => setPeriod(p)}
              className={`px-2 py-1 tracking-widest transition-colors ${
                period === p
                  ? "bg-[#FF007F]/20 text-[#FF007F]"
                  : "text-white/50 hover:text-white"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-8 flex items-center justify-center">
          <Loader2 className="h-4 w-4 text-[#FF007F] animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center">
          <Users className="h-8 w-8 mx-auto text-white/20 mb-2" strokeWidth={1.2} />
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            no gifters yet · be the first
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-white/5">
          {rows.map((g) => {
            const rankStyle = RANK_ACCENT[g.rank] || {};
            const usd = (g.period_cents / 100).toFixed(2);
            return (
              <li
                key={`${g.user_id || "anon"}-${g.rank}`}
                data-testid={`leaderboard-row-${g.rank}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors"
              >
                {/* Rank */}
                <div
                  className="w-7 h-7 flex items-center justify-center font-display font-black text-sm flex-shrink-0"
                  style={{
                    color: rankStyle.label || "#ffffff80",
                    background: rankStyle.bg ? `${rankStyle.bg}18` : "transparent",
                    border: `1px solid ${rankStyle.bg || "rgba(255,255,255,0.15)"}`,
                    boxShadow: rankStyle.ring ? `0 0 12px ${rankStyle.ring}` : "none",
                  }}
                >
                  {g.rank}
                </div>

                {/* Avatar */}
                {g.picture ? (
                  <img
                    src={picUrl(g.picture)}
                    alt=""
                    className="h-9 w-9 object-cover border border-white/10 flex-shrink-0"
                  />
                ) : (
                  <div className="h-9 w-9 flex items-center justify-center bg-[#FF007F]/15 text-[#FF007F] font-display font-black text-sm border border-[#FF007F]/30 flex-shrink-0">
                    {(g.name || "?")[0]?.toUpperCase()}
                  </div>
                )}

                {/* Name + level */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {g.username ? (
                      <Link
                        to={`/u/${g.username}`}
                        data-testid={`leaderboard-user-${g.rank}`}
                        className="text-white text-sm font-semibold truncate hover:text-[#00FFFF] transition-colors"
                      >
                        {g.name}
                      </Link>
                    ) : (
                      <span className="text-white text-sm font-semibold truncate">
                        {g.name}
                      </span>
                    )}
                    {g.gifter?.level > 0 && (
                      <LevelBadge level={g.gifter.level} kind="gifter" size="sm" />
                    )}
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 truncate">
                    {g.tip_count} boost{g.tip_count === 1 ? "" : "s"} · {g.distinct_creators} creator{g.distinct_creators === 1 ? "" : "s"}
                  </p>
                </div>

                {/* Amount */}
                <div className="text-right flex-shrink-0">
                  <p className="font-display font-black text-[#FF007F] text-base flex items-center gap-1 justify-end leading-none">
                    <Zap className="h-3 w-3" fill="#FF007F" strokeWidth={0} />
                    ${usd}
                  </p>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-1">
                    {PERIOD_LABELS[period]}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {compact && rows.length > 0 && (
        <Link
          to="/leaderboard"
          data-testid="leaderboard-view-all"
          className="block text-center py-2 border-t border-white/10 font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-[#FF007F] hover:bg-white/[0.02] transition-colors"
        >
          view.full.leaderboard →
        </Link>
      )}
    </section>
  );
}
